import { useState, useEffect } from 'react';
import { useUIContext } from '../../providers/UIProvider';

export const useCanvasState = () => {
    const [isEmbedMode, setIsEmbedMode] = useState(false);
    const { isDarkMode } = useUIContext();
    const [isPanning, setIsPanning] = useState(false);
    const [isSpacePressed, setIsSpacePressed] = useState(false);
    const [isShiftPressed, setIsShiftPressed] = useState(false);
    const [isAltPressed, setIsAltPressed] = useState(false); // Add Alt key state

    // On mount, check if embed=true is in the URL
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const isEmbed = urlParams.get('embed') === 'true';
        setIsEmbedMode(isEmbed);
    }, []);

    // Handle keyboard events for panning and shift key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Ignore if typing in an input/textarea
            const target = event.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
                return;
            }

            if (event.code === 'Space' && !isSpacePressed) {
                setIsSpacePressed(true);
            }
            if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
                setIsShiftPressed(true);
            }
            if (event.key === 'Alt') { // Check for Alt key
                setIsAltPressed(true);
            }
        };

        const handleKeyUp = (event: KeyboardEvent) => {
             // Ignore if typing in an input/textarea
             const target = event.target as HTMLElement;
             if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
                 return;
             }

            if (event.code === 'Space') {
                setIsSpacePressed(false);
                setIsPanning(false); // Ensure panning stops when space is released
            }
            if (event.code === 'ShiftLeft' || event.code === 'ShiftRight') {
                setIsShiftPressed(false);
            }
            if (event.key === 'Alt') { // Check for Alt key
                setIsAltPressed(false);
            }
        };

        // Handle window blur event to reset key states
        const handleBlur = () => {
            setIsSpacePressed(false);
            setIsPanning(false);
            setIsShiftPressed(false);
            setIsAltPressed(false); // Reset Alt key state on blur
        };

        // Handle window focus event to check if keys are still pressed
        const handleFocus = () => {
            // When window regains focus, check if shift is still pressed
            setIsShiftPressed(false);
            // It's harder to reliably check Alt on focus without complex tracking,
            // so we'll reset it. Users typically release Alt when switching windows.
            setIsAltPressed(false);
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);
        window.addEventListener('focus', handleFocus);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
            window.removeEventListener('focus', handleFocus);
        };
    }, [isSpacePressed]); // Only depend on isSpacePressed

    return {
        isEmbedMode,
        isDarkMode,
        isPanning,
        setIsPanning,
        isSpacePressed,
        isShiftPressed,
        isAltPressed, // Expose Alt key state
    };
};
